import { getPhotoData, removePhotoData, storePhotoData } from './PhotoStorage.js';
import { addQueueRecord, getQueueRecord, getQueueRecordsForJob, removeQueueRecord } from './UploadQueueStore.js';
import { Uploader } from '../../katapult-upload-page/Uploader.js';
import { getAuth } from 'firebase/auth';
import { KatapultJob } from '../../../modules/katapult-job/KatapultJob.js';

/** @import { AssociationConfig, PhotoUpdate, AssociatedPhotoUpdate, Metadata as PhotoMetadata, QueueRecord } from '../PhotoUpload/UploadQueueStore.js' */

const firebaseAppId = globalThis.config.appName;
const firebaseAppConfig = globalThis.config.firebaseConfigs[firebaseAppId];
const isMobile = globalThis.isMobile === true;

let uploader;
/**
 * @returns {Uploader}
 */
function getUploaderInstance() {
  return (uploader ??= new Uploader(firebaseAppConfig, { isMobile, useWorker: true, parallelCount: 1 }));
}

const uploadEventCallbacks = {};

/** @typedef {Object} UploadProgressInfo
 * @property {number} numPhotos -  Total number of photos being uploaded.
 * @property {number} numPhotosUploaded - Number of photos that have been fully uploaded.
 * @property {number} progress - Overall upload progress percentage (0-100).
 * @property {boolean} [complete] - Whether the upload is complete (progress is 100%).
 * @property {Record<string, { progress: number, metadata: QueueRecord['metadata'] }>} uploadProgressByRecord - Progress info for individual records.
 */

/** @type {Record<string, UploadProgressInfo & { uploadProgressByNode?: Record<string, UploadProgressInfo>, uploadProgressBySection?: Record<string, UploadProgressInfo> }>} */
const uploadProgressByJob = {};

/**
 * Generates a unique key for a section based on connectionId and sectionId.
 * @param {string} connectionId
 * @param {string} sectionId
 * @returns {string}
 */
function getSectionKey(connectionId, sectionId) {
  return `${connectionId}:${sectionId}`;
}

/**
 * Retrieves upload progress info for a specific job.
 * @param {string} jobId
 * @returns {UploadProgressInfo}
 */
function getUploadInfoForJob(jobId) {
  const jobUploadInfo = uploadProgressByJob[jobId];
  if (jobUploadInfo) return structuredClone(jobUploadInfo);
  return getEmptyUploadProgressInfo();
}

/**
 * Retrieves upload progress info for a specific node within a job.
 * @param {string} jobId
 * @param {string} nodeId
 * @returns {UploadProgressInfo}
 */
function getUploadInfoForNode(jobId, nodeId) {
  const nodeUploadInfo = uploadProgressByJob[jobId]?.uploadProgressByNode?.[nodeId];
  if (nodeUploadInfo) return structuredClone(nodeUploadInfo);
  return getEmptyUploadProgressInfo();
}

/**
 * Retrieves upload progress info for a specific section within a job.
 * @param {string} jobId
 * @param {string} connectionId
 * @param {string} sectionId
 * @returns {UploadProgressInfo}
 */
function getUploadInfoForSection(jobId, connectionId, sectionId) {
  const sectionKey = getSectionKey(connectionId, sectionId);
  const sectionUploadInfo = uploadProgressByJob[jobId]?.uploadProgressBySection?.[sectionKey];
  if (sectionUploadInfo) return structuredClone(sectionUploadInfo);
  return getEmptyUploadProgressInfo();
}

/**
 * Creates an upload event listener based on the provided configuration.
 * @param {Object} listenerConfig
 * @param {string} listenerConfig.jobId
 * @param {string} [listenerConfig.nodeId]
 * @param {string} [listenerConfig.connectionId]
 * @param {string} [listenerConfig.sectionId]
 * @param {(UploadProgressInfo) => void} listenerConfig.callback
 * @returns {Function} A function to disconnect the listener.
 */
function createUploadEventListener(listenerConfig) {
  const callbackInstanceId = crypto.randomUUID();
  uploadEventCallbacks[callbackInstanceId] = listenerConfig;
  const disconnect = () => delete uploadEventCallbacks[callbackInstanceId];
  return disconnect;
}

/**
 * Listens to upload events for a specific job and invokes the callback with progress updates.
 * @param {string} jobId
 * @param {(UploadProgressInfo) => void} callback
 * @returns {Function} A function to disconnect the listener.
 */
function listenToUploadEventsForJob(jobId, callback) {
  const disconnect = createUploadEventListener({ jobId, callback });
  const jobUploadInfo = getUploadInfoForJob(jobId);
  callback(jobUploadInfo);
  return disconnect;
}

/**
 * Listens to upload events for a specific node within a job and invokes the callback with progress updates.
 * @param {string} jobId
 * @param {string} nodeId
 * @param {(UploadProgressInfo) => void} callback
 * @returns {Function} A function to disconnect the listener.
 */
function listenToUploadEventsForNode(jobId, nodeId, callback) {
  const disconnect = createUploadEventListener({ jobId, nodeId, callback });
  const nodeUploadInfo = getUploadInfoForNode(jobId, nodeId);
  callback(nodeUploadInfo);
  return disconnect;
}

/**
 * Listens to upload events for a specific section within a job and invokes the callback with progress updates.
 * @param {string} jobId
 * @param {string} connectionId
 * @param {string} sectionId
 * @param {(UploadProgressInfo) => void} callback
 * @returns {Function} A function to disconnect the listener.
 */
function listenToUploadEventsForSection(jobId, connectionId, sectionId, callback) {
  const disconnect = createUploadEventListener({ jobId, connectionId, sectionId, callback });
  const sectionUploadInfo = getUploadInfoForSection(jobId, connectionId, sectionId);
  callback(sectionUploadInfo);
  return disconnect;
}

/**
 * Notifies all progress listeners relevant to the given recordId of the current upload progress.
 * @param {string} recordId
 * @returns {Promise<void>}
 */
async function notifyProgressListeners(recordId) {
  for (const listenerConfig of Object.values(uploadEventCallbacks)) {
    const record = await getQueueRecord(recordId);
    if (!record) continue;

    // Notify job listeners.
    if (listenerConfig.jobId === record.jobId && !listenerConfig.nodeId && !listenerConfig.connectionId && !listenerConfig.sectionId) {
      const jobUploadInfo = getUploadInfoForJob(record.jobId);
      listenerConfig.callback(jobUploadInfo);
    }

    // Notify node listeners.
    if (
      listenerConfig.jobId === record.jobId &&
      listenerConfig.nodeId &&
      record.association &&
      'nodeId' in record.association &&
      listenerConfig.nodeId === record.association.nodeId
    ) {
      const nodeUploadInfo = getUploadInfoForNode(record.jobId, listenerConfig.nodeId);
      listenerConfig.callback(nodeUploadInfo);
    }

    // Notify section listeners.
    if (
      listenerConfig.jobId === record.jobId &&
      listenerConfig.connectionId &&
      listenerConfig.sectionId &&
      record.association &&
      'connectionId' in record.association &&
      'sectionId' in record.association &&
      listenerConfig.connectionId === record.association.connectionId &&
      listenerConfig.sectionId === record.association.sectionId
    ) {
      const sectionUploadInfo = getUploadInfoForSection(record.jobId, listenerConfig.connectionId, listenerConfig.sectionId);
      listenerConfig.callback(sectionUploadInfo);
    }
  }
}

/**
 * @typedef {Object} AddPhotoToQueueOptions
 * @property {AssociationConfig} [association] - Configuration for associating the photo after upload.
 * @property {?AssociatedPhotoUpdate} [associatedPhotoUpdate]
 * @property {?PhotoUpdate} [photoUpdate]
 * @property {?PhotoMetadata} [metadata] - Additional metadata to store with the photo upload.
 */

/**
 * Adds a photo to the upload queue.
 * @param {File} file
 * @param {string} jobId
 * @param {AddPhotoToQueueOptions} [options]
 * @returns {Promise<string>} The id of the added queue record.
 */
async function addPhotoToQueue(file, jobId, options) {
  if (!file) throw new Error('file is required to add a photo to the upload queue.');
  if (!jobId) throw new Error('jobId is required to add a photo to the upload queue.');

  const currentUserUid = getAuth().currentUser?.uid;
  if (!currentUserUid) throw new Error('User must be authenticated to add a photo to the upload queue.');

  const queueRecord = {
    jobId,
    filetype: file.type,
    filename: file.name,
    uploadedBy: currentUserUid,
    metadata: options?.metadata,
    association: options?.association,
    associatedPhotoUpdate: options?.associatedPhotoUpdate,
    photoUpdate: options?.photoUpdate
  };

  const recordId = await addQueueRecord(queueRecord);
  await storePhotoData(recordId, file).catch(async (error) => {
    // If storing the photo data fails, remove the queue record to avoid bad records.
    await removeQueueRecord(recordId);
    throw error;
  });

  return recordId;
}

/**
 * Removes a photo from the upload queue and deletes its stored data.
 * @param {string} recordId
 * @returns {Promise<void>}
 */
async function removePhotoFromQueueAndStorage(recordId) {
  await removePhotoData(recordId);
  await removeQueueRecord(recordId);
}

/**
 * Creates an empty UploadProgressInfo object.
 * @returns {UploadProgressInfo}
 */
function getEmptyUploadProgressInfo() {
  return {
    numPhotos: 0,
    numPhotosUploaded: 0,
    progress: 0,
    complete: false,
    uploadProgressByRecord: {}
  };
}

/**
 * Sets the progress for a specific record within the given progress info.
 * @param {UploadProgressInfo} progressInfo
 * @param {QueueRecord} record
 * @param {number} recordProgress
 */
function setRecordProgressAndUpdateProgressInfo(progressInfo, record, recordProgress) {
  progressInfo.uploadProgressByRecord ??= {};
  progressInfo.uploadProgressByRecord[record.id] ??= { progress: 0, metadata: record.metadata };
  progressInfo.uploadProgressByRecord[record.id].progress = recordProgress;

  const recordProgressArray = Object.values(progressInfo.uploadProgressByRecord).map((recordInfo) => recordInfo.progress ?? 0);
  const numPhotos = recordProgressArray.length;
  const numPhotosUploaded = recordProgressArray.filter((p) => p >= 100).length;
  const recordProgressSum = recordProgressArray.reduce((sum, p) => sum + p, 0);
  const progress = numPhotos === 0 ? 0 : recordProgressSum / numPhotos;
  const complete = progress >= 100;
  Object.assign(progressInfo, { numPhotos, numPhotosUploaded, progress, complete });
}

/**
 * Manages and notifies photo upload progress by:
 * - Updating progress info structures.
 * - Notifying relevant listeners of progress updates.
 * - Cleaning up progress info structures when uploads complete.
 * @param {string} recordId
 * @param {number} progress
 * @returns {Promise<void>}
 */
async function notifyPhotoUploadProgress(recordId, progress) {
  const record = await getQueueRecord(recordId);
  if (!record) return;

  // Track progress by job.
  let jobUploadInfo = uploadProgressByJob[record.jobId];
  // Default job upload info or reset it if it is already complete.
  if (jobUploadInfo == null || jobUploadInfo.complete) {
    uploadProgressByJob[record.jobId] = getEmptyUploadProgressInfo();
    jobUploadInfo = uploadProgressByJob[record.jobId];
  }
  setRecordProgressAndUpdateProgressInfo(jobUploadInfo, record, progress);

  // Track progress by node.
  if (record.association && 'nodeId' in record.association) {
    jobUploadInfo.uploadProgressByNode ??= {};
    let nodeUploadInfo = jobUploadInfo.uploadProgressByNode[record.association.nodeId];
    if (nodeUploadInfo == null || nodeUploadInfo.complete) {
      jobUploadInfo.uploadProgressByNode[record.association.nodeId] = getEmptyUploadProgressInfo();
      nodeUploadInfo = jobUploadInfo.uploadProgressByNode[record.association.nodeId];
    }
    setRecordProgressAndUpdateProgressInfo(nodeUploadInfo, record, progress);
  }

  // Track progress by section.
  if (record.association && 'connectionId' in record.association && 'sectionId' in record.association) {
    jobUploadInfo.uploadProgressBySection ??= {};
    const sectionKey = getSectionKey(record.association.connectionId, record.association.sectionId);
    let sectionUploadInfo = jobUploadInfo.uploadProgressBySection[sectionKey];
    if (sectionUploadInfo == null || sectionUploadInfo.complete) {
      jobUploadInfo.uploadProgressBySection[sectionKey] = getEmptyUploadProgressInfo();
      sectionUploadInfo = jobUploadInfo.uploadProgressBySection[sectionKey];
    }
    setRecordProgressAndUpdateProgressInfo(sectionUploadInfo, record, progress);
  }

  await notifyProgressListeners(recordId);
}

/**
 * Uploads (and optionally associates) a photo from the upload queue by adding it to an `Uploader` instance's internal upload queue.
 * Upon successful upload, the photo record is removed from the upload queue store and its stored data is deleted.
 * @param {string} recordId
 * @returns {Promise<string>} The id of the uploaded photo.
 */
async function uploadPhotoFromQueue(recordId) {
  const photoRecord = await getQueueRecord(recordId);
  if (!photoRecord) throw new Error(`No queued photo found with id ${recordId}`);

  const photoData = await getPhotoData(recordId);
  if (!photoData) throw new Error(`No photo data found for queued photo with id ${recordId}`);

  // Create a promise that resolves/rejects when the upload completes/fails.
  // This is done to "promisify" the callback-based `Uploader.uploadFromFiles()` method.
  let resolveUploadComplete;
  let rejectUploadComplete;
  const uploadComplete = new Promise((resolve, reject) => {
    resolveUploadComplete = resolve;
    rejectUploadComplete = reject;
  });

  notifyPhotoUploadProgress(recordId, 0);

  const photoFile = new File([photoData], photoRecord.filename, { type: photoRecord.filetype });
  // TODO: Allow folderId and cameraId to be specified in the queue record.
  const uploadOptions = { jobId: photoRecord.jobId, folderId: 'mobile_upload', cameraId: 'default', uid: photoRecord.uploadedBy };
  const uploader = getUploaderInstance();
  uploader.uploadFromFiles([photoFile], uploadOptions, async (message) => {
    try {
      switch (message.status) {
        case 'failed': {
          // TODO: Handle reporting failure.
          rejectUploadComplete(new Error(message.message ?? 'Photo upload failed'));
          break;
        }
        case 'uploading': {
          // Don't notify progress if upload is complete (will be handled in 'complete' case and double calling with 100 progress causes issues).
          // TODO: Instead of tracking only a percentage, we should also track a complete flag and a failed flag.
          if (message.progress == 100) break;
          notifyPhotoUploadProgress(recordId, message.progress);
          break;
        }
        case 'complete': {
          const photoId = message.photo?._key;

          if (photoRecord.association) {
            const jobRef = KatapultJob.fromId(photoRecord.jobId);

            // Associate the photo to a node.
            if ('nodeId' in photoRecord.association) {
              const { nodeId } = photoRecord.association;
              await jobRef.associatePhotoToNode(nodeId, photoId, true);

              // Apply any associated photo updates to the photo record on the node.
              if (photoRecord.associatedPhotoUpdate) {
                const nodePhotoRef = globalThis.FirebaseWorker.ref(
                  `photoheight/jobs/${photoRecord.jobId}/nodes/${nodeId}/photos/${photoId}`
                );
                await nodePhotoRef.update(photoRecord.associatedPhotoUpdate);
              }
            }

            // Associate the photo to a section.
            if ('connectionId' in photoRecord.association && 'sectionId' in photoRecord.association) {
              const { connectionId, sectionId } = photoRecord.association;
              await jobRef.associatePhotoToSection(sectionId, connectionId, photoId, true);

              // Apply any associated photo updates to the photo record on the section.
              if (photoRecord.associatedPhotoUpdate) {
                const sectionPhotoRef = globalThis.FirebaseWorker.ref(
                  `photoheight/jobs/${photoRecord.jobId}/connections/${connectionId}/sections/${sectionId}/photos/${photoId}`
                );
                await sectionPhotoRef.update(photoRecord.associatedPhotoUpdate);
              }
            }
          }

          // Apply any photo updates to the main photo record.
          if (photoRecord.photoUpdate) {
            const photoRef = globalThis.FirebaseWorker.ref(`photoheight/jobs/${photoRecord.jobId}/photos/${photoId}`);
            await photoRef.update(photoRecord.photoUpdate);
          }

          // Log completed upload progress and notify listeners of the progress change.
          notifyPhotoUploadProgress(recordId, 100);
          // Resolve the upload promise.
          resolveUploadComplete(photoId);
          // Once the upload is complete and listeners are notified, remove the queue record and stored photo data.
          await removePhotoFromQueueAndStorage(recordId);
        }
      }
    } catch (error) {
      rejectUploadComplete(error);
    }
  });

  return uploadComplete;
}

/**
 * @typedef {AddPhotoToQueueOptions} UploadOptions
 */

/**
 * Adds a photo to the upload queue and request it be uploaded (if offline, upload will be deferred).
 * @param {File} file - The photo data to be uploaded.
 * @param {string} jobId - The id of the job to which the photo should be uploaded.
 * @param {UploadOptions} [options] - Additional upload options.
 * @returns {Promise<string>} The id of the uploaded photo.
 */
async function uploadPhoto(file, jobId, options) {
  if (file.type !== 'image/jpeg') {
    throw new Error('Only JPEG images are supported for upload.');
  }

  // If this file is named "image.jpg" (the default name iOS gives to photos), give it a unique name.
  // Otherwise this will result in multiple photos having the same name which is an issue for GetExif() which builds
  // out a name_date string  based on the file name and uses it to determine if the photo already has a key in firebase.
  if (file.name === 'image.jpg') {
    file = new File([file], crypto.randomUUID() + '.jpg', { type: file.type, lastModified: file.lastModified });
  }

  const recordId = await addPhotoToQueue(file, jobId, options);
  const photoId = await uploadPhotoFromQueue(recordId);
  return photoId;
}

/**
 * Uploads all queued photos for a specific job.
 * @param {string} jobId
 * @returns {Promise<void>}
 */
async function uploadQueuedJobPhotos(jobId) {
  const jobPhotoRecordIds = (await getQueueRecordsForJob(jobId)).map((record) => record.id);
  const uploadPromises = jobPhotoRecordIds.map((recordId) => uploadPhotoFromQueue(recordId));
  await Promise.all(uploadPromises);
}

export { uploadPhoto, uploadQueuedJobPhotos, listenToUploadEventsForJob, listenToUploadEventsForNode, listenToUploadEventsForSection };
